import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { Eraser, FileDown, FileSpreadsheet, FolderKanban, Link2, Search, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { DateRangePicker } from '@/components/shared/DateRangePicker';
import { HeaderActions } from '@/components/layout/AppShell';
import { TagBadge } from '@/components/shared/TagBadge';
import { SalesSummaryCards } from './SalesSummaryCards';
import { SalesMonthlyChart } from './SalesMonthlyChart';
import { SalesInvoiceTable } from './SalesInvoiceTable';
import { ReceivablesCard } from './ReceivablesCard';
import { RegisterPaymentDialog, type PaymentDialogTarget } from './RegisterPaymentDialog';
import { MatchIncomingPaymentDialog } from './MatchIncomingPaymentDialog';
import { StatementExportDialog } from './StatementExportDialog';
import { BulkProjectDialog, NO_PROJECT } from '@/components/invoices/BulkProjectDialog';
import { AssignProjectDialog } from '@/components/invoices/AssignProjectDialog';
import { t } from '@/i18n';
import { useAuth } from '@/hooks/useAuth';
import { canExportSalesStatement } from '@/lib/permissions';
import { useSalesFilters } from '@/hooks/useSalesFilters';
import { useSelectedInvoiceItems } from '@/hooks/useSelectedInvoiceItems';
import { useCompaniesApi, useInvoicesApi, useProjectsApi, useTagsApi } from '@/services/api';
import type {
  InvoiceListItem,
  ProjectOption,
  SalesSummary,
  SettlementFilter,
  Tag as TagType,
} from '@ip/shared-types';

interface SalesViewProps {
  companyNip: string;
  companyName: string;
  onInvoiceSelect: (invoiceId: string) => void;
}

const YEAR_COUNT = 5;

function SalesSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
      <Skeleton className="h-[340px]" />
    </div>
  );
}

export function SalesView({ companyNip, onInvoiceSelect }: SalesViewProps) {
  const [summary, setSummary] = useState<SalesSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [tags, setTags] = useState<TagType[]>([]);
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [total, setTotal] = useState(0);

  // Filters live in the URL (like the cost list's useInvoiceFilters) so they
  // survive navigating into an invoice detail and back.
  const {
    year, setYear,
    search, setSearch,
    dateRange, setDateRange,
    settlementFilter, setSettlementFilter,
    projectFilter, setProjectFilter,
    selectedTags, setSelectedTags,
    page, setPage,
    pageSize, setPageSize,
    hasActiveFilters,
    clearAllFilters,
  } = useSalesFilters();

  // Task B3: project filter — hidden when the project list is inaccessible.
  const [projects, setProjects] = useState<ProjectOption[]>([]);

  // Task A3: payment registration + receivables refresh coupling.
  const [paymentTarget, setPaymentTarget] = useState<PaymentDialogTarget | null>(null);
  const [matchPaymentOpen, setMatchPaymentOpen] = useState(false);
  const [statementOpen, setStatementOpen] = useState(false);
  const [receivablesRefresh, setReceivablesRefresh] = useState(0);

  // Customer statement PDF — module 'sales' without a tag restriction.
  const { permissions, isSuperAdmin } = useAuth();
  const showStatement = isSuperAdmin || canExportSalesStatement(permissions, companyNip);

  // Bulk project assignment — selection + dialog, mirroring the cost list.
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const selectedItems = useSelectedInvoiceItems(invoices, selectedRowKeys);
  const [bulkProjectModalOpen, setBulkProjectModalOpen] = useState(false);
  // '' = nothing chosen, NO_PROJECT = clear the assignment, else a project id.
  const [bulkProjectSelection, setBulkProjectSelection] = useState('');
  const [bulkUpdating, setBulkUpdating] = useState(false);

  // Single-row "Przypisz do projektu" action — target invoice + in-flight flag.
  const [projectTarget, setProjectTarget] = useState<InvoiceListItem | null>(null);
  const [projectAssigning, setProjectAssigning] = useState(false);

  const loadSeq = useRef(0);

  const { getSalesSummary } = useCompaniesApi();
  const { getInvoices, updateInvoiceTags, bulkAssignProject } = useInvoicesApi();
  const { getTags } = useTagsApi();
  const { listProjectOptions, assignInvoiceProject } = useProjectsApi();

  const yearOptions = Array.from({ length: YEAR_COUNT }, (_, i) => new Date().getFullYear() - i);

  // Summary + monthly chart — reloaded whenever the year selector changes.
  const loadSummary = useCallback(async () => {
    if (!companyNip) return;
    setSummaryLoading(true);
    try {
      const result = await getSalesSummary(companyNip, year);
      setSummary(result);
    } catch {
      toast.error(t('errors.loadingFailed'));
    } finally {
      setSummaryLoading(false);
    }
  }, [companyNip, year, getSalesSummary]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // Sales tags (module 'sales') — loaded once per company.
  useEffect(() => {
    if (!companyNip) return;
    getTags(companyNip, 'sales')
      .then(setTags)
      .catch((error) => console.error('Failed to load sales tags:', error));
  }, [companyNip, getTags]);

  // Projects for the filter (Task B3) — a 403 (tag-restricted user) just
  // leaves the filter hidden. The selected project itself lives in the URL
  // and must survive remounts (mirrors InvoiceList).
  useEffect(() => {
    if (!companyNip) return;
    let mounted = true;
    setProjects([]);
    listProjectOptions(companyNip)
      .then((data) => {
        if (mounted) setProjects(data);
      })
      .catch(() => {
        /* no project-list access — hide the filter */
      });
    return () => {
      mounted = false;
    };
  }, [companyNip, listProjectOptions]);

  // Sales invoice list — server-driven pagination, independent of the year selector
  // so switching years never hides invoices; filters scope by date of sale.
  const loadInvoices = useCallback(async () => {
    if (!companyNip) return;
    const seq = ++loadSeq.current;
    setInvoicesLoading(true);
    try {
      const result = await getInvoices(companyNip, {
        direction: 'sale',
        search: search || undefined,
        dateType: 'sale_date',
        dateFrom: dateRange?.from ? dayjs(dateRange.from).format('YYYY-MM-DD') : undefined,
        dateTo: dateRange?.to ? dayjs(dateRange.to).format('YYYY-MM-DD') : undefined,
        settlement: settlementFilter === 'all' ? undefined : settlementFilter,
        projectId: projectFilter === 'all' ? undefined : projectFilter,
        tagIds: selectedTags.length > 0 ? selectedTags : undefined,
        page,
        pageSize,
        sortBy: 'issueDate',
        sortOrder: 'desc',
      });
      if (seq === loadSeq.current) {
        setInvoices(result.items);
        setTotal(result.total);
      }
    } catch {
      toast.error(t('errors.loadingFailed'));
    } finally {
      if (seq === loadSeq.current) setInvoicesLoading(false);
    }
  }, [
    companyNip,
    search,
    dateRange,
    settlementFilter,
    projectFilter,
    selectedTags,
    page,
    pageSize,
    getInvoices,
  ]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // Resetting to page 1 on filter changes now happens inside the
  // useSalesFilters setters — an effect here would also fire on remount and
  // destroy a page number restored from the URL.

  // Tag filter chips — toggling adds/removes a tag id from the URL-persisted
  // selection, mirroring InvoiceList's toggleTagFilter for the cost list.
  const toggleTagFilter = useCallback(
    (tagId: string) => {
      setSelectedTags((prev) =>
        prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId],
      );
    },
    [setSelectedTags],
  );

  const handleTagChange = useCallback(
    async (invoiceId: string, currentTags: string[], tagId: string, checked: boolean) => {
      const newTags = checked ? [...currentTags, tagId] : currentTags.filter((id) => id !== tagId);
      try {
        await updateInvoiceTags(companyNip, invoiceId, newTags);
        setInvoices((prev) => prev.map((inv) => (inv.id === invoiceId ? { ...inv, tags: newTags } : inv)));
      } catch (error) {
        toast.error('Failed to update tags');
        console.error(error);
      }
    },
    [companyNip, updateInvoiceTags],
  );

  // Selection must not leak between companies.
  useEffect(() => {
    setSelectedRowKeys([]);
  }, [companyNip]);

  // Bulk project assignment (manual provenance). NO_PROJECT clears.
  const handleBulkAssignProject = useCallback(async () => {
    if (selectedRowKeys.length === 0 || !bulkProjectSelection) return;
    setBulkUpdating(true);
    try {
      const projectId = bulkProjectSelection === NO_PROJECT ? null : bulkProjectSelection;
      const result = await bulkAssignProject(companyNip, selectedRowKeys, projectId);
      toast.success(t('invoices.bulk.projectSuccess', { count: result.updated }));
      setBulkProjectModalOpen(false);
      setBulkProjectSelection('');
      setSelectedRowKeys([]);
      loadInvoices();
    } catch {
      toast.error(t('invoices.bulk.projectError'));
    } finally {
      setBulkUpdating(false);
    }
  }, [selectedRowKeys, bulkProjectSelection, companyNip, bulkAssignProject, loadInvoices]);

  // Single-invoice project assignment (row action) — whole-invoice endpoint,
  // MANUAL provenance; null clears. On success the row is patched in place
  // (no full reload), mirroring what the backend would return.
  const handleAssignProjectSingle = useCallback(
    async (projectId: string | null) => {
      if (!projectTarget) return;
      setProjectAssigning(true);
      try {
        await assignInvoiceProject(companyNip, projectTarget.id, projectId);
        const projectName = projectId
          ? (projects.find((p) => p.id === projectId)?.name ?? null)
          : null;
        setInvoices((prev) =>
          prev.map((inv) =>
            inv.id === projectTarget.id
              ? { ...inv, projectId, projectName, projectAssignedBy: projectId ? 'manual' : null }
              : inv,
          ),
        );
        toast.success(t('invoices.assignProject.success'));
        setProjectTarget(null);
      } catch {
        toast.error(t('invoices.assignProject.error'));
      } finally {
        setProjectAssigning(false);
      }
    },
    [projectTarget, companyNip, projects, assignInvoiceProject],
  );

  const handleRegisterPayment = useCallback((invoice: InvoiceListItem) => {
    setPaymentTarget({
      invoiceId: invoice.id,
      invoiceNumber: invoice.invoiceNumber,
      currency: invoice.currency,
      outstanding: invoice.outstanding,
    });
  }, []);

  const handlePaymentSaved = useCallback(() => {
    loadInvoices();
    setReceivablesRefresh((k) => k + 1);
  }, [loadInvoices]);

  const initialLoading = summaryLoading && invoicesLoading && !summary;
  // No sales invoices anywhere (unfiltered, year-agnostic) — friendlier than an
  // empty table + all-zero cards, mirrors CompanyDashboard's per-year empty state.
  const noSalesAtAll = !invoicesLoading && !summaryLoading && total === 0 && !hasActiveFilters;

  return (
    <div className="flex flex-col gap-4">
      <HeaderActions>
        <Button variant="outline" size="sm" asChild>
          <Link to={`/${companyNip}/sales/report`}>
            <FileSpreadsheet className="size-4" />
            {t('sales.report.button')}
          </Link>
        </Button>
        {showStatement && (
          <Button variant="outline" size="sm" onClick={() => setStatementOpen(true)}>
            <FileDown className="size-4" />
            {t('sales.statement.button')}
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => setMatchPaymentOpen(true)}>
          <Link2 className="size-4" />
          {t('invoices.combinedPayment.salesButton')}
        </Button>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger size="sm" className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {yearOptions.map((y) => (
              <SelectItem key={y} value={String(y)}>
                {y}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </HeaderActions>

      {initialLoading && <SalesSkeleton />}

      {!initialLoading && noSalesAtAll && (
        <div className="text-muted-foreground flex flex-col items-center gap-3 py-20">
          <TrendingUp className="size-10 opacity-50" />
          <p className="text-sm">{t('sales.noData')}</p>
        </div>
      )}

      {!initialLoading && !noSalesAtAll && (
        <>
          {summary && <SalesSummaryCards summary={summary} />}
          {summary && <SalesMonthlyChart months={summary.months} year={year} />}

          {/* Task A3: receivables by customer — row click filters the list below */}
          <ReceivablesCard
            companyNip={companyNip}
            refreshKey={receivablesRefresh}
            onCustomerClick={(customer) => setSearch(customer.customerName)}
          />

          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-56">
                <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                <Input
                  placeholder={t('sales.searchPlaceholder')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <DateRangePicker value={dateRange} onChange={setDateRange} />
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                value={settlementFilter}
                onValueChange={(v) => v && setSettlementFilter(v as SettlementFilter | 'all')}
                aria-label={t('sales.settlement.filterLabel')}
              >
                {(['all', 'unpaid', 'overdue', 'partial', 'paid'] as const).map((value) => (
                  <ToggleGroupItem key={value} value={value} className="px-3">
                    {t(`sales.settlement.${value}`)}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
              {projects.length > 0 && (
                <Select value={projectFilter} onValueChange={setProjectFilter}>
                  <SelectTrigger size="sm" className="w-44" aria-label={t('sales.columns.project')}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('invoices.filters.projectAll')}</SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        <span className="flex items-center gap-2">
                          <span
                            className="inline-block size-2 shrink-0 rounded-full border"
                            style={{ backgroundColor: project.color ?? 'transparent' }}
                            aria-hidden="true"
                          />
                          <span className="truncate">
                            {project.name}
                            {project.status === 'closed'
                              ? ` ${t('projects.selector.closedSuffix')}`
                              : ''}
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {hasActiveFilters && (
                <Button variant="outline" size="sm" className="ml-auto" onClick={clearAllFilters}>
                  <Eraser className="size-4" />
                  {t('sales.filters.clearAll')}
                </Button>
              )}
            </div>

            {tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-muted-foreground mr-1 text-xs">
                  {t('sales.columns.tags')}:
                </span>
                {tags.map((tag) => (
                  <TagBadge
                    key={tag.id}
                    name={tag.name}
                    color={tag.color}
                    selected={selectedTags.includes(tag.id)}
                    onClick={() => toggleTagFilter(tag.id)}
                  />
                ))}
              </div>
            )}

            {/* Minimal bulk bar (project assignment only), mirroring the cost
                list's BulkActionsBar container. Hidden when there are no
                projects — assignment is the only bulk action here. */}
            {selectedRowKeys.length > 0 && projects.length > 0 && (
              <div className="bg-primary/5 border-primary/20 flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2">
                <span className="text-sm font-medium">
                  {t('invoices.bulk.selected', { count: selectedRowKeys.length })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={bulkUpdating}
                  onClick={() => setBulkProjectModalOpen(true)}
                >
                  <FolderKanban className="size-4" />
                  {t('invoices.bulk.assignToProject')}
                </Button>
              </div>
            )}

            <SalesInvoiceTable
              invoices={invoices}
              tags={tags}
              loading={invoicesLoading}
              total={total}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onRowClick={onInvoiceSelect}
              onTagChange={handleTagChange}
              onRegisterPayment={handleRegisterPayment}
              canAssignProject={projects.length > 0}
              onAssignProject={setProjectTarget}
              selectedIds={selectedRowKeys}
              onSelectionChange={setSelectedRowKeys}
              selectedItems={selectedItems}
            />
          </div>
        </>
      )}

      <RegisterPaymentDialog
        companyNip={companyNip}
        target={paymentTarget}
        onOpenChange={(open) => !open && setPaymentTarget(null)}
        onSaved={handlePaymentSaved}
      />

      <MatchIncomingPaymentDialog
        companyNip={companyNip}
        open={matchPaymentOpen}
        onOpenChange={setMatchPaymentOpen}
        onLinked={handlePaymentSaved}
      />

      <StatementExportDialog
        companyNip={companyNip}
        open={statementOpen}
        onOpenChange={setStatementOpen}
      />

      <AssignProjectDialog
        target={projectTarget}
        projects={projects}
        updating={projectAssigning}
        onOpenChange={(open) => {
          if (!open) setProjectTarget(null);
        }}
        onSubmit={handleAssignProjectSingle}
      />

      <BulkProjectDialog
        open={bulkProjectModalOpen}
        onOpenChange={(open) => {
          setBulkProjectModalOpen(open);
          if (!open) setBulkProjectSelection('');
        }}
        projects={projects}
        selection={bulkProjectSelection}
        onSelectionChange={setBulkProjectSelection}
        count={selectedRowKeys.length}
        updating={bulkUpdating}
        onSubmit={handleBulkAssignProject}
      />
    </div>
  );
}
